import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import AdminWorkspaceShell from './AdminWorkspaceShell';
import { useAuth } from '../contexts/AuthContext';
import { effectiveRoles } from './ProtectedRoute';
import { ambassadors } from '../services/ambassadors';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';

const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
  </svg>
);

const PeopleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
  </svg>
);

const SalesManagementIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const TeamIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const SlideshowIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2" />
  </svg>
);

const ArticleIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const PodcastsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const SchoolIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  </svg>
);

const PortalIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253M9 10h.01M9 13h.01" />
  </svg>
);

const BriefcaseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v1m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const AssessmentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

const CategoryIcon = () => (
  <CategoryRoundedIcon fontSize="small" />
);

const InvoiceIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);


const VideoLibraryIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

const LogsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const UploadCenterIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const MessageIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-2 8H5a2 2 0 01-2-2V7a2 2 0 012-2h14a2 2 0 012 2v7a2 2 0 01-2 2z" />
  </svg>
);

type MenuItem = { text: string; icon: React.ReactNode; path: string };

/**
 * منوی چند نقشی: اجتماع آیتم‌های همهٔ نقش‌های کاربر، بدون تکرار.
 *
 * ترتیب از نقش اصلی شروع می‌شود تا برای کسی که فقط یک نقش دارد هیچ چیزی
 * جابه‌جا نشود؛ آیتم‌های نقش‌های افزوده بعد از آن و فقط اگر تازه باشند
 * اضافه می‌شوند (کلید یکتایی: path).
 */
const getMenuItemsForRoles = (roles: string[]): MenuItem[] => {
  const seen = new Set<string>();
  const merged: MenuItem[] = [];
  for (const role of roles) {
    for (const item of getMenuItems(role)) {
      if (seen.has(item.path)) continue;
      seen.add(item.path);
      merged.push(item);
    }
  }
  return merged;
};

const getMenuItems = (userRole: string): MenuItem[] => {
  const dashboardPath = (userRole === 'SALES_MANAGER' || userRole === 'SALES_PERSON') 
    ? '/sales-dashboard' 
    : '/dashboard';
  
  const baseItems = [
    { text: 'داشبورد', icon: <DashboardIcon />, path: dashboardPath },
  ];

  if (userRole === 'ADMIN') {
    return [
      ...baseItems,
      { text: 'دسته‌بندی‌ها', icon: <CategoryIcon />, path: '/dashboard/categories' },
      { text: 'مدیریت کاربران', icon: <PeopleIcon />, path: '/users-management' },
      { text: 'مدیریت فروشندگان', icon: <SalesManagementIcon />, path: '/sales-management' },
      { text: 'کارگاه‌ها', icon: <SchoolIcon />, path: '/workshops' },
      { text: 'اسلایدرها', icon: <SlideshowIcon />, path: '/sliders' },
      { text: 'وبلاگ (مقالات)', icon: <ArticleIcon />, path: '/articles' },
      { text: 'پادکست‌ها', icon: <PodcastsIcon />, path: '/podcasts' },
      { text: 'دوره‌ها', icon: <SchoolIcon />, path: '/courses' },
      { text: 'پرسشنامه‌ها', icon: <AssessmentIcon />, path: '/assessments' },
      { text: 'پرسشنامه‌های VIP', icon: <AssessmentIcon />, path: '/vip-surveys' },
      { text: 'پرونده‌های کسب‌وکار', icon: <BriefcaseIcon />, path: '/business-cases' },
      { text: 'شاخص‌های استخدام', icon: <BriefcaseIcon />, path: '/recruitment/criteria' },
      { text: 'متقاضیان استخدام', icon: <BriefcaseIcon />, path: '/recruitment/submissions' },
      { text: 'پرتال رشد ۶C', icon: <PortalIcon />, path: '/portal-admin' },
      { text: 'کوپن تخفیف', icon: <InvoiceIcon />, path: '/coupons' },
      { text: 'سفارشات', icon: <InvoiceIcon />, path: '/orders' },
      { text: 'فاکتورها', icon: <InvoiceIcon />, path: '/invoices' },
      { text: 'ویدیو پادکست‌ها', icon: <VideoLibraryIcon />, path: '/video-podcasts' },
      { text: 'نظرات', icon: <ArticleIcon />, path: '/comments' },
      { text: 'پیام‌ها', icon: <MessageIcon />, path: '/messages' },
      { text: 'لیدها', icon: <PeopleIcon />, path: '/leads' },
      { text: 'کیف پول', icon: <InvoiceIcon />, path: '/wallet-requests' },
      { text: 'تراکنش‌های کیف پول', icon: <InvoiceIcon />, path: '/wallet-transactions' },
      { text: 'مرکز آپلود', icon: <UploadCenterIcon />, path: '/upload-center' },
      { text: 'لاگ‌ها', icon: <LogsIcon />, path: '/logs' },
      { text: 'تنظیمات', icon: <SettingsIcon />, path: '/settings' },
    ];
  } else if (userRole === 'SALES_MANAGER') {
    return [
      ...baseItems,
      { text: 'کارگاه‌ها', icon: <SchoolIcon />, path: '/workshops' },
    ];
  } else if (userRole === 'MENTOR') {
    /* منتور عمداً منوی کوتاهی دارد؛ بقیهٔ پنل ادمین برایش معنایی ندارد */
    return [
      { text: 'پنل منتور', icon: <PortalIcon />, path: '/portal-mentor' },
      { text: 'پرونده‌های کسب‌وکار', icon: <BriefcaseIcon />, path: '/business-cases' },
    ];
  } else if (userRole === 'STORE') {
    /* فروشگاه فقط همین دو بخش را دارد؛ داشبورد و بقیهٔ پنل برایش معنایی ندارد */
    return [
      { text: 'پرسشنامه‌ها', icon: <AssessmentIcon />, path: '/assessments' },
      { text: 'پرونده‌های کسب‌وکار', icon: <BriefcaseIcon />, path: '/business-cases' },
    ];
  } else if (userRole === 'SALES_PERSON') {
    return [
      ...baseItems,
      { text: 'کارگاه‌های من', icon: <SchoolIcon />, path: '/my-workshops' },
      { text: 'مشتریان من', icon: <PeopleIcon />, path: '/my-customers' },
    ];
  }

  return baseItems;
};

function resolvePageTitle(pathname: string, roles: string[]): string {
  if (pathname === '/ambassadors') return 'مدیریت سفیران';
  if (pathname === '/dashboard/categories') return 'استودیو دسته‌بندی';

  const role = roles[0] ?? 'USER';
  const items = getMenuItemsForRoles(roles);
  const exact = items.find((item) => item.path === pathname);
  if (exact) return exact.text;

  const byPathLength = [...items].sort((a, b) => b.path.length - a.path.length);
  const nested = byPathLength.find((item) => item.path.length > 1 && pathname.startsWith(`${item.path}/`));
  if (nested) return nested.text;

  if (role === 'ADMIN') return 'پنل مدیریت';
  if (role === 'MENTOR') return 'پنل منتور';
  if (role === 'STORE') return 'پنل فروشگاه';
  if (role === 'SALES_MANAGER' || role === 'SALES_PERSON') return 'داشبورد';
  return 'پنل';
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const menuRoles = useMemo(() => {
    const roles = effectiveRoles(user as any);
    return roles.length ? roles : ['USER'];
  }, [user]);
  const [ambassadorAccess, setAmbassadorAccess] = useState(false);
  useEffect(() => {
    let live = true;
    setAmbassadorAccess(false);
    if (user) ambassadors.context().then(context => {
      if (live) setAmbassadorAccess(context.roles.length > 0);
    }).catch(() => {});
    return () => { live = false; };
  }, [user]);
  const menuItems = useMemo(() => [
    ...getMenuItemsForRoles(menuRoles),
    ...(ambassadorAccess ? [{ text: 'مدیریت سفیران', icon: <PeopleIcon />, path: '/ambassadors' }] : []),
  ], [menuRoles, ambassadorAccess]);
  const pageTitle = useMemo(() => resolvePageTitle(location.pathname, menuRoles), [location.pathname, menuRoles]);
  return <AdminWorkspaceShell menuItems={menuItems} pageTitle={pageTitle}>{children}</AdminWorkspaceShell>;
};

export default Layout;
